import {
  Badge,
  Box,
  Flex,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';

import type { TalentDiscoveryRespUserV3 } from '@revelio/data-access';

import { useCurrencyFormatter } from './hooks/useCurrencyFormatter';
import type { EmployerHealth } from './hooks/useEmployerHealth';
import type { ResolvedSkill } from './hooks/useMapUserSkills';
import type { ResolvedProfileIds } from './hooks/useResolveProfileIds';
import { RISK_COLOR_SCHEMES } from './utils/colors';

interface CareerSnapshotProps {
  currentUser: TalentDiscoveryRespUserV3 | null;
  resolvedIds: ResolvedProfileIds;
  resolvedSkills: ResolvedSkill[];
  userSalary: number;
  userPercentile: number | null;
  currentEmployerHealth: EmployerHealth | undefined;
  totalRoleSkills: number;
  isLoading: boolean;
}

export const CareerSnapshot = ({
  currentUser,
  resolvedIds,
  resolvedSkills,
  userSalary,
  userPercentile,
  currentEmployerHealth,
  totalRoleSkills,
  isLoading,
}: CareerSnapshotProps) => {
  const formatCurrency = useCurrencyFormatter();

  if (isLoading || !currentUser) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        p={5}
        border="1px solid"
        borderColor="gray.100"
        borderLeft="3px solid"
        borderLeftColor="green.400"
        boxShadow="0 1px 8px rgba(45, 66, 106, 0.06)"
      >
        <HStack spacing={6}>
          <Skeleton height="20px" width="200px" borderRadius="md" />
          <Skeleton height="20px" width="150px" borderRadius="md" />
          <Skeleton height="20px" width="120px" borderRadius="md" />
        </HStack>
      </Box>
    );
  }

  const matchedSkillCount = resolvedSkills.length;

  let percentileColor = 'gray';
  if (userPercentile !== null) {
    if (userPercentile >= 60) {
      percentileColor = 'green';
    } else if (userPercentile >= 40) {
      percentileColor = 'yellow';
    } else {
      percentileColor = 'red';
    }
  }

  return (
    <Box
      bg="white"
      borderRadius="xl"
      p={5}
      border="1px solid"
      borderColor="gray.100"
      borderLeft="3px solid"
      borderLeftColor="green.400"
      boxShadow="0 1px 8px rgba(45, 66, 106, 0.06)"
    >
      <Flex wrap="wrap" gap={6} align="center" justify="space-between">
        {/* Profile basics */}
        <VStack align="start" spacing={0.5} minW="200px">
          <Text fontSize="sm" color="navyBlue.600" fontWeight="medium">
            {resolvedIds.roleName ??
              currentUser.current_title ??
              'Role unknown'}
          </Text>
          <Text fontSize="xs" color="navyBlue.300">
            {resolvedIds.companyName ?? ''}
            {resolvedIds.companyName && resolvedIds.primaryGeo
              ? ' \u00B7 '
              : ''}
            {resolvedIds.primaryGeo?.name ?? ''}
          </Text>
        </VStack>

        {/* Salary + Percentile */}
        <HStack spacing={4}>
          {userSalary > 0 && (
            <VStack align="center" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="navyBlue.700">
                {formatCurrency(userSalary)}
              </Text>
              <Text fontSize="2xs" color="navyBlue.300">
                Salary
              </Text>
            </VStack>
          )}
          {userPercentile !== null && (
            <VStack align="center" spacing={0}>
              <Badge
                colorScheme={percentileColor}
                fontSize="md"
                px={2}
                py={0.5}
                borderRadius="md"
              >
                {userPercentile}th
              </Badge>
              <Text fontSize="2xs" color="navyBlue.300">
                Percentile
              </Text>
            </VStack>
          )}
        </HStack>

        {/* Employer health */}
        {currentEmployerHealth && (
          <VStack align="center" spacing={0}>
            <Badge
              colorScheme={
                RISK_COLOR_SCHEMES[currentEmployerHealth.risk] ?? 'gray'
              }
              fontSize="xs"
              variant="subtle"
            >
              {currentEmployerHealth.risk} risk
            </Badge>
            <Text fontSize="2xs" color="navyBlue.300">
              Employer stability
            </Text>
          </VStack>
        )}

        {/* Skills summary */}
        {totalRoleSkills > 0 && (
          <VStack align="center" spacing={0}>
            <Text fontSize="sm" fontWeight="semibold" color="navyBlue.600">
              {matchedSkillCount}/{totalRoleSkills}
            </Text>
            <Text fontSize="2xs" color="navyBlue.300">
              Role skills
            </Text>
          </VStack>
        )}
      </Flex>
    </Box>
  );
};
